import { useState, useEffect } from "react";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";

import { getInvoicePaymentIntent } from "apis";
import { withRedux, priceToDollars, displayError } from "helpers";

import styled, { css } from "react-emotion";
import InvoiceError from "./InvoiceError";
import WebIcon from "../../pages/Web/WebIcon";

const inlineBlock = css`
  display: inline-block;
`;

const paraStyle = css`
  text-align: left;
  margin-left: 15px;
`;

const CardElementContainer = styled("div")`
  height: 40px;
  display: flex;
  align-items: center;
  & .StripeElement {
    width: 100%;
    padding: 15px;
  }
`;
const sectionText = css`
  text-align: left;
  font-size: 1em;
  margin: 0px;
  margin-bottom: 20px;
  @media only screen and (min-width: 768px) {
    text-align: left;
    font-size: 1.25em;
    margin: 0px;
    margin-bottom: 20px;
  }
`;

const storeContainer = css`
  margin: 10px;
  @media only screen and (min-width: 768px) {
    margin: 10px 40px;
  }
`;

const H3 = styled("h3")`
  font-family: "Lucida Grande", "Lucida Sans Unicode", "Lucida Sans", Geneva,
    Verdana, sans-serif;
  font-size: 20px;
  line-height: 18px;
  color: #1f3747;
  margin: auto;
`;

const SubmitButton = styled("button")`
  display: block;
  height: 40px;
  width: 100%;
  font-size: inherit;
  font-weight: bold;
  background-color: ${(props) => (props.disabled ? "#adadad" : "#89d2d3")};
  border: none;
  box-shadow: 3px 3px 13px rgba(120, 120, 120, 0.5);
  font-family: "Lucida Grande", "Lucida Sans Unicode", "Lucida Sans", Geneva,
    Verdana, sans-serif;
  border-radius: 4px;
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  color: #fff;
  cursor: pointer;
  margin-top: 50px;
`;

const Title = styled("p")`
  text-align: left;
  margin-bottom: 10px;
  font-weight: bold;
  font-size: 1.17em;
`;

const cardErrors = css`
  color: #f17f7f;
`;

// Custom styling can be passed to options when creating an Element.
const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: "#32325d",
      fontSmoothing: "antialiased",
      fontSize: "16px",
      "::placeholder": {
        color: "#aab7c4",
      },
    },
    invalid: {
      color: "#F17F7F",
      iconColor: "#F17F7F",
    },
  },
  hidePostalCode: true,
};

const InvoicePaymentForm = (props) => {
  const [error, setError] = useState(null);

  const [isProcessing, setProcessingTo] = useState(false);

  const [invoiceError, setInvoiceError] = useState("");
  const [cardFormComplete, setCardFormComplete] = useState(false);

  const [paid, setPaid] = useState(status === "PAID");
  const stripe = useStripe();
  const elements = useElements();
  const { reqBody, customer, isAdmin, total, appId, invoiceId, status } = props;

  // console.log("card form complete: " + cardFormComplete);

  // Handle real-time validation errors from the card Element.
  const handleChange = (event) => {
    // console.dir(event);
    if (event.error) {
      setError(event.error.message);
    } else {
      setError(null);
    }
    setCardFormComplete(event.complete);
  };

  const getClientSecret = async () => {
    // create payment intent on the server
    // client_secret of that payment intent

    let clientSecretObject = await getInvoicePaymentIntent(
      appId,
      invoiceId
    ).then((res) => {
      // console.dir(res);
      return res;
    });

    // clientSecretObject = JSON.parse(clientSecretObject);

    // if (clientSecretObject.errorCode) {
    //   setInvoiceError(clientSecretObject.errorMessage);
    //   displayError(clientSecretObject.errorMessage);
    //   setProcessingTo(false);
    //   return;
    // }

    return clientSecretObject;
  };

  const invalidCustomerInfo = () => {
    let error = "";
    if (customer.firstName === "") {
      error = "First name for customer info cannot be blank.";
    } else if (customer.lastName === "") {
      error = "Last name for customer info cannot be blank.";
    } else if (customer.email === "") {
      error = "Email for customer info cannot be blank.";
    } else if (customer.phone === "") {
      error = "Phone number for customer info cannot be blank.";
    }
    if (error != "") {
      setInvoiceError(error);
      displayError(error);
      return true;
    } else {
      return false;
    }
  };

  // Handle form submission.
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }
    if (invalidCustomerInfo()) return;

    // console.dir(event);

    const billingDetails = {
      name: customer.fullName,
      email: customer.email,
    };

    const cardElement = elements.getElement("card");

    setProcessingTo(true);
    try {
      // need stripe.js
      // need reference to cardElement
      // create a payment method
      const paymentMethodReq = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
        billing_details: billingDetails,
      });

      if (paymentMethodReq.error) {
        setInvoiceError(paymentMethodReq.error.message);
        displayError(paymentMethodReq.error.message);
        setProcessingTo(false);
        return;
      }
      // console.log("got payment method req");
      // console.dir(paymentMethodReq);
      // create payment intent on the server
      // client_secret of that payment intent

      const clientSecretObject = await getClientSecret();
      const clientSecret = clientSecretObject.stripeClientSecret;

      const { error } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: paymentMethodReq.paymentMethod.id,
      });

      if (error) {
        setInvoiceError(error.message);
        displayError(error.message);
        setProcessingTo(false);
        return;
      }

      // success!
      // setProcessingTo(false);
      // goToConfirm(clientSecretObject);
      setPaid(true);
    } catch (e) {
      console.error(e);
      setInvoiceError(e.message);
      displayError(e.message);
    }
  };

  const disabled = isProcessing || !stripe || isAdmin;
  // console.log("disabled pay button: " + disabled);

  return (
    <div className={storeContainer}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          marginBottom: "20px",
        }}
      >
        {paid ? (
          <div style={{ display: "flex", alignItems: "center" }}>
            <WebIcon
              iconName={"checkMark"}
              useColor={true}
              size={"100px"}
              hover={false}
              className={inlineBlock}
              customColor={"#4caf50"}
            />
            <p className={paraStyle}>
              Thanks for doing business with us! Your invoice has been
              successfully paid. We've emailed you a confirmation.{" "}
            </p>
          </div>
        ) : (
          <div>
            {" "}
            <H3 className={sectionText}>Credit or debit card</H3>
            <form onSubmit={handleSubmit}>
              <CardElementContainer>
                <CardElement
                  id="card-element"
                  options={CARD_ELEMENT_OPTIONS}
                  onChange={handleChange}
                />
              </CardElementContainer>

              {invoiceError && <InvoiceError>{invoiceError}</InvoiceError>}
              <SubmitButton
                disabled={isProcessing || !stripe || !cardFormComplete}
              >
                {isProcessing
                  ? "Processing..."
                  : "Pay " + priceToDollars(total)}
              </SubmitButton>
            </form>
          </div>
        )}{" "}
      </div>
    </div>
  );
};

export default withRedux(InvoicePaymentForm);
